import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs } from '@mantine/core'
import { useMedplum } from '@medplum/react'
import { IconLayoutKanban, IconRoute, IconShieldLock } from '@tabler/icons-react'
import { useBoard, useBoardList } from '@/pages/Boards/hooks'
import { BoardAdminTab } from './BoardAdminTab'
import { CareJourneysTab } from './CareJourneys/CareJourneysTab'
import { CareQueueTab } from './CareQueueTab'
import { COMPLETED_GROUP_LABEL } from './groups'
import { readLastBoard, readLastTab, writeLastBoard, writeLastTab } from './lastBoardStorage'
import { MONO, QUEUE_COLOR, SANS } from './queueTheme'

// Pill tabs from the approved board design. These go through inline `style`
// rather than classes on purpose: Mantine's own `.mantine-Tabs-tab` rules are
// unlayered CSS and would outrank any Tailwind utility (see queueTheme).
const tabStyle = (active: boolean) => ({
  borderRadius: 8,
  padding: '9px 15px',
  fontSize: 14,
  fontWeight: active ? 500 : 400,
  background: active ? '#eef3fb' : 'transparent',
  color: active ? QUEUE_COLOR.wait : '#5a6472',
  border: 'none',
})

/** Count chip inside the active tab: white on the tab's soft blue. */
const TabCount = ({ value, active }: { value: number; active: boolean }) => (
  <span
    className="rounded-full px-[7px] py-[2px] text-[11.5px]"
    style={{
      fontFamily: MONO,
      background: active ? '#ffffff' : '#eef1f6',
      color: active ? QUEUE_COLOR.wait : QUEUE_COLOR.muted,
    }}
  >
    {value}
  </span>
)

export const CareCoordinationPage = () => {
  const [params, setParams] = useSearchParams()
  const medplum = useMedplum()
  const isSuperAdmin = medplum.isSuperAdmin()
  // Care Queue is the tab coordinators live in, so it's the default — and the
  // fallback for stale values like the removed 'completed' and 'appointments'
  // tabs. 'board-admin' is only a valid value for super admins.
  //
  // With no explicit ?tab= (navigating back from another page, which drops the
  // query string), restore the tab this user was last on instead of resetting
  // them to Care Queue. Same treatment the selected board already gets below.
  const userId = medplum.getProfile()?.id
  const requestedTab = params.get('tab') ?? (userId ? readLastTab(userId) : null)
  const tab =
    requestedTab === 'care-journeys'
      ? 'care-journeys'
      : requestedTab === 'board-admin' && isSuperAdmin
        ? 'board-admin'
        : 'care-queue'

  const { data: boardsData } = useBoardList()
  const boards = boardsData?.boards ?? []
  // Prefer the requested board, but fall back to the first one when the param is
  // stale (e.g. the selected board was just deleted) so we never get stuck on a
  // board that no longer exists. resolvedBoard is truthy only when the requested
  // id is in the caller's own org-scoped list — super admins included, since
  // they're org-scoped in both boardList and Board admin and boardGet 404s any
  // cross-org id — so the boards[0] fallback can never surface another org's board.
  const requestedBoard = params.get('board')
  const resolvedBoard = boards.find((b) => b.id === requestedBoard)
  // With no explicit ?board= (e.g. a fresh login), restore the last board this
  // user viewed if it still exists in their org; otherwise fall back to the first.
  const rememberedId = !requestedBoard && userId ? readLastBoard(userId) : null
  const remembered = rememberedId ? boards.find((b) => b.id === rememberedId) : undefined
  const boardId = resolvedBoard?.id ?? remembered?.id ?? boards[0]?.id
  const { data: boardData } = useBoard(boardId)

  // Remember whichever board the user is actually viewing — a dropdown pick, a
  // ?board= deep link (e.g. from a notification), or the restored default — so
  // returning later (even after logout) reopens it. boardId is only ever a real
  // board in the caller's list, so no extra validation is needed here.
  useEffect(() => {
    if (userId && boardId) writeLastBoard(userId, boardId)
  }, [userId, boardId])

  // Remember the resolved tab (not the raw param) so a stale or unauthorised
  // value is never persisted — a non-super-admin who lands on ?tab=board-admin
  // stores 'care-queue', which is what they actually saw.
  useEffect(() => {
    if (userId) writeLastTab(userId, tab)
  }, [userId, tab])

  // Only open a notification's linked task when its own board resolved — never
  // carry the `item` param onto the fallback board (a deleted/inaccessible board
  // would otherwise land the user on an unrelated board).
  const initialItemId = resolvedBoard ? params.get('item') : null

  const completedGroupId = boardData?.board.groups.find(
    (g) => g.label === COMPLETED_GROUP_LABEL,
  )?.id
  const todoItems = boardData?.items.filter((item) => item.groupId !== completedGroupId) ?? []

  const setParam = (key: string, value: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set(key, value)
      return next
    })
  }

  return (
    <div className="flex flex-col gap-5 p-5" style={{ fontFamily: SANS }}>
      <div className="flex flex-col gap-[5px]">
        <h1
          className="text-[24px] font-semibold tracking-[-0.015em]"
          style={{ color: QUEUE_COLOR.ink }}
        >
          Care Coordination
        </h1>
        <p className="text-sm" style={{ color: '#5a6472' }}>
          Manage appointments, follow-ups and care tasks.
        </p>
      </div>

      <Tabs
        variant="pills"
        value={tab}
        onChange={(value) => value && setParam('tab', value)}
        keepMounted={false}
      >
        <Tabs.List className="gap-1">
          <Tabs.Tab
            value="care-queue"
            style={tabStyle(tab === 'care-queue')}
            leftSection={<IconLayoutKanban size={15} />}
            rightSection={
              todoItems.length > 0 ? (
                <TabCount value={todoItems.length} active={tab === 'care-queue'} />
              ) : undefined
            }
          >
            Care Queue
          </Tabs.Tab>
          <Tabs.Tab
            value="care-journeys"
            style={tabStyle(tab === 'care-journeys')}
            leftSection={<IconRoute size={15} />}
          >
            Care Journeys
          </Tabs.Tab>
          {isSuperAdmin && (
            <Tabs.Tab
              value="board-admin"
              style={tabStyle(tab === 'board-admin')}
              leftSection={<IconShieldLock size={15} />}
            >
              Board admin
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="care-journeys" pt="md">
          <CareJourneysTab />
        </Tabs.Panel>
        <Tabs.Panel value="care-queue" pt="md">
          <CareQueueTab
            boardId={boardId}
            boards={boards}
            onSelectBoard={(id) => setParam('board', id)}
            initialItemId={initialItemId}
          />
        </Tabs.Panel>
        {isSuperAdmin && (
          <Tabs.Panel value="board-admin" pt="md">
            <BoardAdminTab />
          </Tabs.Panel>
        )}
      </Tabs>
    </div>
  )
}
